import { LockIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Flex, SectionCard, Text } from '@/components/common';
import type { ProfilePhoto } from '@/types';
import { PhotoThumbnail } from './PhotoThumbnail';

/** Owner's photos as thumbnails with per-photo like counts; pro only. */
export function LikesStatsSection({
  photos,
  counts,
  hasSubscription,
  onUnlock,
}: {
  photos: ProfilePhoto[];
  counts: Map<string, number>;
  hasSubscription: boolean;
  onUnlock: () => void;
}) {
  const { t } = useTranslation('chat');

  if (photos.length === 0) return null;

  return (
    <SectionCard title={t('likes.statsTitle')}>
      <Box position="relative">
        <Flex
          gap="2"
          wrap="wrap"
          justify={hasSubscription ? undefined : 'center'}
          py={hasSubscription ? undefined : '5'}
          aria-hidden={!hasSubscription || undefined}
          style={
            hasSubscription
              ? undefined
              : { filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' }
          }
        >
          {photos.map(photo => (
            <PhotoThumbnail
              key={photo.id}
              photo={photo}
              count={counts.get(photo.id) ?? 0}
              disabled={!hasSubscription}
            />
          ))}
        </Flex>
        {!hasSubscription && (
          <Flex
            position="absolute"
            inset="0"
            direction="column"
            align="center"
            justify="center"
            gap="1"
            px="3"
            onClick={onUnlock}
            style={{ cursor: 'pointer' }}
            data-testid="stats-locked"
          >
            <LockIcon size={16} />
            <Text size="2" weight="bold" align="center">
              {t('likes.statsLocked')}
            </Text>
            <Text size="1" color="gray" align="center">
              {t('likes.statsLockedHint')}
            </Text>
            <Button
              type="button"
              size="sm"
              radius="full"
              onClick={onUnlock}
              className="mt-2"
              data-testid="stats-unlock"
            >
              {t('likes.statsUnlockCta')}
            </Button>
          </Flex>
        )}
      </Box>
    </SectionCard>
  );
}
